<template>
  <header class="site-header">
 <div class="container-72">
  <!-- Row 1: brand + profile -->
  <div class="row-top">
    <!-- Brand -->
    <router-link to="/" class="brand" aria-label="Robot Cycle Time AI — Home">
      <img :src="logoUrl" class="logo-icon" alt="" />
      <span class="brand-text">Robot Cycle Time AI</span>
    </router-link>

    <!-- Profile / Account -->
    <div class="menu" ref="menuRef">
      <button
        type="button"
        class="menu-btn profile-btn"
        aria-label="Open account menu"
        @click="toggle"
        @keydown.down.prevent="open = true"
        @keydown.esc.prevent="close"
        aria-haspopup="true"
        :aria-expanded="open ? 'true' : 'false'"
      >
        <span class="avatar">{{ initials }}</span>
        <span class="user-label">{{ shortName }}</span>
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
          <path d="M5 7l5 6 5-6H5z" />
        </svg>
      </button>

      <div v-if="open" class="dropdown" role="menu">
        <template v-if="isSignedIn">
          <div class="group-label">Account</div>
          <router-link to="/account" class="item" role="menuitem" @click="close">Account</router-link>
          <router-link to="/pricing" class="item" role="menuitem" @click="close">View Subscription</router-link>
          <router-link to="/contact" class="item" role="menuitem" @click="close">Contact</router-link>
          <button class="item danger" role="menuitem" @click="navigateDelete">Delete Account</button>
          <div class="separator"></div>
          <button class="item" role="menuitem" @click="onSignOut">Sign out</button>
        </template>
        <template v-else>
          <div class="group-label">Welcome</div>
          <button class="item" role="menuitem" @click="openAuth('signin')">Sign in</button>
          <button class="item" role="menuitem" @click="openAuth('signup')">Sign up</button>
          <router-link to="/contact" class="item" role="menuitem" @click="close">Contact</router-link>
        </template>
      </div>
    </div>
  </div>

  <!-- Row 2: primary navigation -->
  <nav class="row-bottom nav" aria-label="Primary">
    <router-link class="nav-link" to="/how-to">How to Use</router-link>
    <router-link class="nav-link" to="/route">Calculator</router-link>
    <router-link class="nav-link" to="/pricing">Pricing</router-link>
  </nav>
</div>


    <!-- Auth modal -->
    <AuthModal
      :open="showAuth"
      :initial-mode="authMode"
      :title="authMode === 'signup' ? 'Create Account' : 'Welcome'"
      @close="showAuth = false"
      @signed-in="showAuth = false"
    />
  </header>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import AuthModal from '@/components/AuthModal.vue'

/* ← your SVG placed in src/assets (rename as needed) */
import logoUrl from '@/assets/robot-logo.svg'

const route = useRoute()
const auth = useAuthStore()
const { user, profile } = storeToRefs(auth)
onMounted(() => auth.init())

const isSignedIn = computed(() => !!user.value)
const shortName = computed(() => {
  if (!isSignedIn.value) return 'Guest'
  return profile.value?.displayName || user.value?.displayName || user.value?.email || 'Signed in'
})
const initials = computed(() => {
  const s = shortName.value.trim()
  if (!s) return 'G'
  const [a, b] = s.split(/\s+/)
  return ((a?.[0] || '') + (b?.[0] || s?.[0] || 'G')).toUpperCase()
})

const open = ref(false)
const menuRef = ref(null)
function toggle(){ open.value = !open.value }
function close(){ open.value = false }

function onDocClick(e){ if (menuRef.value && !menuRef.value.contains(e.target)) close() }
function onKeydown(e){ if (e.key === 'Escape') close() }
onMounted(() => {
  document.addEventListener('click', onDocClick, { passive: true })
  document.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKeydown)
})
watch(() => route.fullPath, () => close())

async function onSignOut(){ try { await auth.signOut() } finally { close() } }
function navigateDelete(){ window.location.href = '/account?tab=delete'; close() }

const showAuth = ref(false)
const authMode = ref('signin')
function openAuth(mode){ authMode.value = mode || 'signin'; showAuth.value = true; close() }
</script>

<style scoped>
.site-header{
  position: sticky; top:0; z-index:2000;
  background:#5C6680; backdrop-filter: blur(6px);
  box-shadow:0 1px 0 rgba(0,0,0,.06);
  /* iOS notch: full inset; no change on web/Android (env(...)=0) */
  padding-top: env(safe-area-inset-top);
}

.container-72{ max-width:72rem; margin-inline:auto; padding-inline:1rem; }
/* Row 1: brand + profile button */
.row-top{
  display:flex; align-items:center; justify-content:space-between;
  padding: .52rem 0 .14rem;
}

/* Row 2: nav links in their own line */
.row-bottom.nav{
  display:flex; align-items:center; gap:.75rem;
  padding: .10rem 0 .26rem;
  border-top: none;
}

/* Allow horizontal scroll on very small phones; no wrap */
@media (max-width: 640px){
  .row-bottom.nav{
    overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none;
  }
  .row-bottom.nav::-webkit-scrollbar{ display:none; }
}

/* Brand */
.brand{ display:inline-flex; align-items:center; gap:.4rem; text-decoration:none; color:#F8FAFC; }
.logo-icon{ width:28px; height:28px; flex:0 0 auto; display:block;} 
.brand-text{ font-weight:800; letter-spacing:.2px; white-space:nowrap; }
.ai-pill{ margin-left:.35rem; padding:.05rem .45rem; border-radius:999px; background:#9BB1CF; color:#fff; font-size:.8em; font-weight:800; }

/* Nav */
.nav{ display:flex; align-items:center; gap:1rem; }
.nav-link{ color:#F8FAFC; opacity:.95; text-decoration:none; padding:.25rem .4rem; border-radius:.5rem; white-space:nowrap; }

.nav-link:hover{ text-decoration:underline; }
:deep(.router-link-active).nav-link{ text-decoration:underline; }

/* Account menu */
.menu{ position:relative; }
.menu-btn{
  display:inline-flex; align-items:center; gap:.5rem;
  background:#f3f4f6; border:1px solid #e5e7eb; border-radius:999px;
  padding:.3rem .6rem; cursor:pointer;
}
.menu-btn svg{ fill:#6b7280; }
.avatar{
  width:22px; height:22px; border-radius:999px;
  background:#111827; color:#fff; display:inline-grid; place-items:center;
  font-size:12px; line-height:1;
}
.user-label{
  max-width:160px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  font-size:.85rem; color:#111827;
}

/* dropdown */
.dropdown{
  position:absolute; right:0; top:calc(100% + 8px);
  min-width:220px; background:#fff; border:1px solid #e5e7eb; border-radius:12px;
  box-shadow:0 8px 24px rgba(16,24,40,.1); padding:8px; z-index:50;
}
.group-label{ font-size:.7rem; letter-spacing:.3px; text-transform:uppercase; opacity:.6; padding:6px 8px 4px; }
.item{
  width:100%; text-align:left; display:block;
  padding:8px 10px; border-radius:8px; color:#111827; background:transparent; border:none; cursor:pointer;
  text-decoration:none;
}
.item:hover{ background:#f3f4f6; }
.item.danger{ color:#b91c1c; }
.separator{ height:1px; background:#e5e7eb; margin:6px 4px; }
/* Tiny mobile fix: reduce crowding & enable smooth horizontal scroll */
@media (max-width: 640px) {
  .container-72 { padding-inline: .75rem; }
  .brand-text { font-size: .95rem; }
  .nav { gap: .4rem; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .user-label { display: none; }     /* show avatar only on small screens */
  .menu-btn { padding: .35rem .5rem; }
}


/* ensure the dropdown is on top of cards/background */
.dropdown { z-index: 3000; }

/* keep children unclipped so the dropdown can extend */
.nav { overflow: visible; }  /* keep this even on mobile */
/* --- Profile button tap-target fix (iOS friendly) --- */
.profile-btn {
  min-width: 44px;              /* Apple HIG size */
  min-height: 44px;
  padding: .35rem .6rem;        /* keeps current look, just enlarges hit area */
  position: relative;
  z-index: 40;                  /* above neighboring links */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  pointer-events: auto;
}

/* Ensure children don't “steal” hit-testing in WKWebView */
.profile-btn .avatar,
.profile-btn svg {
  pointer-events: none;         /* click anywhere on the button works */
}

/* Keep the menu stacking sane */
.menu { position: relative; z-index: 35; }
.dropdown { z-index: 3000; }    /* you already have this; safe to keep */

.nav-link{ padding:.25rem .4rem; }          /* was .25 .25 */
@media (max-width: 640px){
  .nav{ gap:.4rem; }
}
</style>
